/**
 * Password change — CU-86akkg3gu Parts 1 and 2.
 *
 * ── ⚠️ WHAT WAS BROKEN, AND FOR WHOM ──────────────────────────────────────
 *
 * 1. **SMS-enrolled patients could not change their password AT ALL.**
 *    `reauthenticateWithCredential` throws `auth/multi-factor-auth-required` on a
 *    CORRECT password — it is control flow, not an error. The old blanket
 *    `.catch` swallowed it with no code inspection, so the form simply sat
 *    there. Same bug class as CU-86akjgb8p (`/concierge`).
 *
 * 2. The inner `.catch` enumerated six auth codes in a comment and
 *    `multi-factor-auth-required` was absent from the list.
 *
 * 3. **No Sentry signal was possible.** Both `.catch` blocks `throw` inside a
 *    floating promise, so the enclosing `try/catch` never saw it, the rethrow
 *    became an unhandled rejection, and `captureException` was UNREACHABLE from
 *    either path. `async/await` here is what makes the rejection paths real.
 *
 * ── ⚠️ AFTER THE CHALLENGE, DO NOT RE-RUN reauthenticateWithCredential ────
 *
 * `resolveSignIn` DOES advance `auth_time` — measured on staging with a real
 * SMS-enrolled admin. A re-drive CANNOT succeed: Identity Platform re-applies
 * the MFA policy to a fresh reauthentication and throws
 * `auth/multi-factor-auth-required` AGAIN, the very state the challenge just
 * exited. The console shipped that version and made the path **impossible to
 * complete for every SMS-enrolled admin — two of three — with every test passing
 * throughout** (removed 2026-08-12, `e1864c05`, PR #468).
 *
 * The invariant is *"an explicit fresh step-up happened just now"*, NOT
 * *"`reauthenticateWithCredential` was the function that ran"*. A completed SMS
 * challenge is STRONGER proof than a re-typed password. See
 * `usePasskeyEnrollment.ts:595-640` — this is the third repo to write it down.
 *
 * ⚠️ RE-READ `currentUser` AFTER `resolveSignIn`. The SDK hands back a fresh
 * user object; `updatePassword` must target THAT one, or it silently addresses a
 * stale session.
 *
 * ── ⚠️ THE WARNING COPY IS FACTOR-AWARE BECAUSE THE FACTS DIFFER ──────────
 *
 * Measured (CU-86akjurnv Addendum 6): a password change REAPS PASSKEYS (lazily,
 * via H4, on next use) and **SMS SURVIVES** — `mfaInfo` was unchanged across the
 * change. So *"changing your password will clear your second factors"* is FALSE
 * for an SMS holder and would push them to re-enrol a factor they still have.
 */

import { FormEvent, useState } from 'react';
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  getMultiFactorResolver,
} from 'firebase/auth';
// Type-only: `MultiFactorError` is not a runtime export (matches
// `usePasskeyEnrollment.ts:4`).
import type { MultiFactorError } from 'firebase/auth';
import { auth } from '../../constants/firebaseModular';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../buttons';
import { PasswordInput } from './PasswordInput';
import { MfaChallengeForm } from '../auth/MfaChallengeForm';
import type { Resolver } from '../../hooks/useMfaChallenge';
import { scrubSafeAuthCode } from '../../utils/mfa/sentryAuthCode';
import { suspendRevocationChecks } from '../../utils/mfa/sessionRevocationCheck';
import * as Sentry from '@sentry/browser';

/**
 * What the patient holds, for the warning copy.
 *
 * ⚠️ `null` MEANS UNKNOWN, NEVER "NO". A failed or in-flight credential read
 * must not render a claim about factors we could not observe. Telling someone
 * with no SMS factor that "your text-message codes will keep working" is a false
 * assurance on a security screen. Same rule as CU-86ak8ydf2: unknown ⇒ never act
 * on an assumption.
 *
 * `PasskeyEnrollmentSection` already carries this three-state distinction
 * (`credentials !== null && !hasCredentials`, `:334-336`) — this mirrors it
 * rather than flattening to a boolean.
 */
export interface FactorState {
  hasPasskey: boolean | null;
  hasSms: boolean | null;
  /**
   * ⚠️ WHETHER A SECOND FACTOR IS REQUIRED (`mfa.enforced`).
   *
   * Shapes ONE sentence: only an enforced patient is signed out after the
   * change, because only their new token's missing AAL2 claim costs them
   * access. Promising a sign-out to an unenforced patient would be false.
   */
  mfaEnforced?: boolean;
}

/**
 * ⚠️ EXPORTED DELIBERATELY, DESPITE THE `react-refresh` WARNING. The copy matrix
 * has five states (passkey-only / SMS-only / both / neither / UNKNOWN) and is
 * the part most likely to be got wrong, so it is unit-tested directly rather
 * than only through five renders. The warning costs a hot-reload nicety; a
 * false factor claim on a security screen costs a patient.
 *
 * ⚠️ EVERY SENTENCE HERE IS MEASURED. Do not add a consequence that has not been
 * observed on staging — this is copy a patient acts on mid-security-action.
 */
export function passwordChangeWarning(factors: FactorState): string | null {
  const { hasPasskey, hasSms } = factors;

  // Unknown ⇒ say nothing about factors at all.
  if (hasPasskey === null) return null;
  if (!hasPasskey) return null; // SMS-only or neither: no passkey warning applies.

  // ⚠️ "If you would like to continue using a passkey" — NOT "you will need to".
  // A passkey is optional; an SMS holder is still AAL2-capable without one, and
  // an imperative reads as an instruction the patient must obey to keep access.
  // Operator note, staging 2026-09-23.
  const base =
    'Changing your password will remove your saved passkeys. If you would like to continue using a passkey, you can add one again afterwards, and remove the old one from your password manager.';

  // ⚠️ Only assert the SMS half when SMS is KNOWN present. `null` here means we
  // could not read it, and a false reassurance is worse than silence.
  //
  // ⚠️ THE SIGN-IN SENTENCE IS NOT DECORATION — IT DESCRIBES A REAL DETOUR.
  // Measured on staging 2026-09-23: after the passkey is reaped, the next token
  // carries no AAL2 claim, so the MFA gate fires. The gate offers only
  // ENROLLMENT, and "set up a texted code" is refused for a patient who already
  // has one — it has no challenge path (zero references to `MfaChallengeForm`).
  // A sign-out/sign-in DOES recover them, via the challenge on the login path.
  //
  // So this sentence points at the route that works. ⚠️ It is a mitigation for
  // a gate defect this task does not fix — CU-86aknw0ku. If the gate gains a
  // challenge path, revisit this wording rather than leaving it stale.
  // ⚠️ "YOU WILL BE SIGNED OUT" — NOT "you may be asked". Earlier wording
  // hedged, because at the time we did not know what actually happened. We do
  // now: an enforced patient's new token carries no AAL2 claim, so the app signs
  // them out deliberately (see `applyNewPassword`). Copy that says "may" for
  // something certain is how a patient reads a real consequence as a maybe.
  const smsHalf =
    hasSms === true ? ' Your text-message codes will keep working.' : '';

  // ⚠️ ONLY PROMISED WHEN IT IS TRUE. The sign-out is conditional on
  // enforcement, so this sentence must be too.
  const signOutHalf = factors.mfaEnforced
    ? ' You will be signed out afterwards and will need to sign in again with your new password.'
    : '';

  return `${base}${smsHalf}${signOutHalf}`;
}

type Stage = 'idle' | 'form' | 'challenge' | 'done';

export const PasswordUpdateForm = ({
  factors = { hasPasskey: null, hasSms: null },
  onOpen,
  willSignOut = false,
  onSignOut,
}: {
  factors?: FactorState;
  /**
   * ⚠️ WILL THIS PATIENT LOSE ACCESS WHEN THEIR TOKEN DROPS BELOW AAL2?
   *
   * ⚠️ NOT simply `mfa.enforced`. `firestore.rules` `gated()` is satisfied by
   * THREE things — `hasSecondFactor()`, an UNEXPIRED `hasAttestedExemption()`,
   * or `notEnforced()`. A patient riding a live exception keeps access without
   * AAL2, so signing them out would be gratuitous.
   *
   * ⚠️ Post-cutover there are no unenforced patients — only patients with
   * exceptions — so `enforced` alone is the wrong question. The host computes
   * this; see `profile.tsx`. Mirrors mobile's prop of the same name.
   *
   * Advisory for COPY and for the post-change hand-off, never for access (M4).
   */
  willSignOut?: boolean;
  /** ⚠️ Ends the session after a successful change, when enforced. */
  onSignOut?: () => void;
  /**
   * ⚠️ RE-READ FACTOR STATE WHEN THE FORM OPENS. The warning is a factual claim
   * computed from `factors`, and the profile page hosts `PasskeyEnrollmentSection`
   * directly above this form — a patient can enrol or delete a passkey and reach
   * this form seconds later. Without this the copy describes the state at page
   * load: it would tell a patient who JUST enrolled that they have no passkey to
   * lose, and then silently destroy it.
   *
   * Found by adversarial review, 2026-09-23.
   */
  onOpen?: () => void;
} = {}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [stage, setStage] = useState<Stage>('idle');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [resolver, setResolver] = useState<Resolver | null>(null);
  const { currentUser } = useAuth();

  const warning = passwordChangeWarning({ ...factors, mfaEnforced: willSignOut });

  const meetConstraints = (password: string): boolean => {
    let alertString = 'Invalid password:\n';
    if (password.length < 8) {
      alertString = alertString.concat('- Password must be at least 8 characters\n');
    }
    if (password.length > 25) {
      alertString = alertString.concat('- Password must be at most 25 characters\n');
    }
    if (!/[A-Z]/.test(password)) {
      alertString = alertString.concat('- Password must contain at least one uppercase letter\n');
    }
    if (!/[a-z]/.test(password)) {
      alertString = alertString.concat('- Password must contain at least one lowercase letter\n');
    }
    if (!/[0-9]/.test(password)) {
      alertString = alertString.concat('- Password must contain at least one number\n');
    }
    if (!/[!@#$%^&*]/.test(password)) {
      alertString = alertString.concat('- Password must contain at least one special character (! @ # $ % ^ & *)\n');
    }

    if (alertString !== 'Invalid password:\n') {
      alert(alertString);
      return false;
    } else {
      return true;
    }
  };

  /** Clear the typed secrets and report success. */
  function finish() {
    setCurrentPassword('');
    setNewPassword1('');
    setNewPassword2('');
    setResolver(null);
    setStage('done');
  }

  /**
   * Apply the new password.
   *
   * ⚠️ Takes the user explicitly so the post-challenge path can pass the FRESH
   * object from `auth.currentUser` rather than a pre-challenge reference.
   */
  async function applyNewPassword(user: NonNullable<typeof currentUser>) {
    try {
      // ⚠️ RE-ARM AROUND THE CALL ITSELF, NOT JUST BEFORE IT.
      //
      // The window opened at submit is a WALL CLOCK the ceremony can outrun. A
      // slow `updatePassword` — poor network, an SDK retry, a backgrounded
      // mobile tab — can exceed 10s, at which point `validSince` has already
      // moved, the fresh token is still settling, and the next focus/route
      // check sends the stale one. That is the exact defect the suspension was
      // added to fix (staging 2026-09-23), one timing layer further out.
      //
      // ⚠️ AND AGAIN AFTER IT RESOLVES. `updatePassword` returning is not the
      // end of the churn: the SDK is still settling the new token, and a check
      // landing in THAT gap fails the same way.
      //
      // Found by adversarial review, 2026-09-23.
      suspendRevocationChecks();
      await updatePassword(user, newPassword1);
      suspendRevocationChecks();

      // ── ⚠️ THE SESSION IS NOW BELOW AAL2. END IT DELIBERATELY. ────────────
      //
      // CU-86aknw0ku. `updatePassword` moves `validSince`, so the SDK mints a
      // FRESH token — from a password-only refresh, so it carries NO
      // `firebase.sign_in_second_factor` and no `aal2`. `firestore.rules`
      // `gated()` reads that claim, so an ENFORCED patient holds a valid
      // session with NO PHI access.
      //
      // ⚠️ THE PORTAL HID THIS, WHICH MADE IT WORSE. In-app navigation serves
      // PHI from React Query CACHE, so nothing re-reads and nothing fails: the
      // patient browses normally on a session that cannot actually read
      // Firestore. A refresh does not BREAK it — a refresh REVEALS it, by
      // dropping them on the MFA gate with no explanation. Same
      // cached-navigation blindness as CU-86akjurnv Addendum 2.
      //
      // Made visible on mobile 2026-09-23 (uid 37xWewnnMISeNUTNVhJkflm6MI92,
      // SMS-only), where no cache stood in the way and it surfaced immediately
      // as continuous permission-denied.
      //
      // ⚠️ WE CANNOT RE-MINT THE CLAIM. AAL2 comes from Identity Platform or
      // the passkey bridge (`authAssurance.ts`), and a password change destroys
      // the former. Ending the session is the only honest remedy — the patient
      // re-authenticates and the login challenge mints a genuine AAL2 token.
      //
      // ⚠️ ONLY WHEN ENFORCED. An unenforced patient keeps full access on a
      // password-only token; ejecting them would be gratuitous.
      if (willSignOut && onSignOut) {
        setStage('done');
        onSignOut();
        return;
      }

      finish();
    } catch (error) {
      // ⚠️ REACHABLE NOW. The old code threw inside a floating promise, so this
      // capture could never run from either path.
      const code = (error as { code?: string })?.code ?? '';
      Sentry.captureException(error, {
        tags: { feature: 'password-update', step: 'update', auth_error_code: scrubSafeAuthCode(code) },
      });
      setErrors(['We could not update your password. Please try again, or contact info@mideohealth.com.']);
      setStage('form');
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setErrors([]);

    if (meetConstraints(newPassword1) === false) return;
    if (newPassword1 !== newPassword2) {
      alert('New passwords do not match');
      return;
    }
    if (!currentUser || !currentUser.email) return;

    // ── ⚠️ CONFIRM AT SUBMIT — THE LAST MOMENT A CHOICE EXISTS ──────────────
    //
    // The amber notice on open informs while the patient types; this ASKS.
    // After `updatePassword` succeeds the sign-out is unavoidable, so telling
    // them then is information they cannot act on.
    //
    // ⚠️ ONLY WHEN THEY WILL ACTUALLY BE SIGNED OUT. A patient riding a live
    // exception keeps access, and a confirm with no consequence behind it is
    // friction that teaches people to click through dialogs.
    //
    // ⚠️ `window.confirm` is SYNCHRONOUS and BLOCKING — unlike mobile's
    // `Alert.alert`, which needs an explicit callback. Same behaviour, and
    // cancelling (including via Escape) returns false, so a dismissal cannot
    // proceed. Mirrors `PasswordUpdateForm.confirmSignOut` on mobile.
    if (
      willSignOut &&
      !window.confirm(
        "Change your password?\n\nYou'll be signed out and will need to sign in again with your new password."
      )
    ) {
      return;
    }

    // ⚠️ SUSPEND THE REVOCATION CHECK ACROSS THIS CEREMONY.
    //
    // `updatePassword` moves `validSince`, and for a moment the old token is
    // still in flight. A check landing in that gap sends the STALE token,
    // Firebase rejects it, and the patient is signed out ONE SECOND AFTER
    // successfully changing their password. Measured on staging 2026-09-23 —
    // see `sessionRevocationCheck.ts`.
    //
    // ⚠️ SET BEFORE THE REAUTH, not just around `updatePassword`. The SMS
    // challenge sits between them, and its screen transitions fire `focus` and
    // `route` triggers.
    //
    // ⚠️ RE-ARMED after the challenge too — the grace window is short and the
    // challenge can take longer than it.
    suspendRevocationChecks();

    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);

    try {
      await reauthenticateWithCredential(currentUser, credential);
    } catch (error) {
      const code = (error as { code?: string })?.code ?? '';

      // ── THE SMS-ENROLLED PATIENT BRANCH ──────────────────────────────────
      //
      // ⚠️ NOT AN ERROR — THE PASSWORD WAS CORRECT. Identity Platform throws
      // this on a correct password whenever a second factor is enrolled.
      // Rendering "that password didn't match" over a perfectly good password is
      // the bug class that locked an enrolled admin out of staging 2026-07-30.
      if (code === 'auth/multi-factor-auth-required') {
        const mfaResolver = getMultiFactorResolver(
          auth,
          error as MultiFactorError
        ) as unknown as Resolver | null;

        if (!mfaResolver) {
          // The SDK gave us the error but no resolver — nothing to render a
          // challenge from. A real fault, not an ordinary outcome.
          Sentry.captureException(error, {
            tags: { feature: 'password-update', step: 'mfa-resolver' },
          });
          setErrors(['Something went wrong. Please try again in a moment.']);
          return;
        }

        // ⚠️ BREADCRUMB, NOT captureException. This is the expected path for
        // every SMS-enrolled patient; capturing it would flood Sentry with
        // correct behaviour.
        Sentry.addBreadcrumb({
          category: 'password-update',
          level: 'info',
          message: 'password change: second factor required',
        });
        setResolver(mfaResolver);
        setStage('challenge');
        return;
      }

      const isBadPassword = code === 'auth/wrong-password' || code === 'auth/invalid-credential';
      const isThrottled = code === 'auth/too-many-requests';

      if (isBadPassword) {
        setErrors(['That password is not correct. Please try again.']);
      } else if (isThrottled) {
        // No duration (CU-86ajtgauu): Google publishes none, and the one block
        // we have measured (MIDEO-UNIFIED-88) lasted HOURS, not minutes.
        setErrors(['Too many attempts. Please wait a while and try again.']);
      } else {
        // ⚠️ REACHABLE NOW — genuine faults only, never the MFA code above.
        Sentry.captureException(error, {
          tags: { feature: 'password-update', step: 'reauth', auth_error_code: scrubSafeAuthCode(code) },
        });
        setErrors(['Something went wrong. Please try again, or contact info@mideohealth.com.']);
      }
      return;
    }

    await applyNewPassword(currentUser);
  }

  function handleCancel() {
    setErrors([]);
    setResolver(null);
    setStage('idle');
  }

  // ── THE SECOND-FACTOR CHALLENGE ─────────────────────────────────────────
  if (stage === 'challenge' && resolver) {
    return (
      <div className="mt-6">
        <p className="mb-4 text-sm">
          For your security, please confirm the code we just texted you before changing your password.
        </p>
        <MfaChallengeForm
          resolver={resolver}
          onCancel={handleCancel}
          onComplete={() => {
            // ⚠️ NO reauthenticateWithCredential RE-DRIVE HERE. See the header:
            // it re-applies the MFA policy and throws the same error again,
            // which is an infinite loop. `resolveSignIn` already advanced
            // `auth_time` — measured.
            //
            // ⚠️ FRESH USER OBJECT — the SDK replaces it after `resolveSignIn`.
            // ⚠️ RE-ARM: the challenge may have outlived the window opened at
            // submit, and `updatePassword` is still to come.
            suspendRevocationChecks();
            const fresh = auth.currentUser;
            if (!fresh) {
              Sentry.captureMessage('password-update: no user after challenge', {
                level: 'warning',
                tags: { feature: 'password-update', step: 'post-challenge' },
              });
              setErrors(['Something went wrong. Please try again.']);
              setStage('form');
              return;
            }
            void applyNewPassword(fresh);
          }}
        />
      </div>
    );
  }

  return (
    <div className="mt-6">
      {stage === 'idle' && (
        <Button
          id="update-password"
          type="button"
          textContent="Update Password"
          onClick={() => {
            // ⚠️ BEFORE rendering the warning, not after — see `onOpen`.
            onOpen?.();
            setStage('form');
          }}
        />
      )}

      {stage === 'done' && (
        <div data-testid="password-updated">
          <p className="font-semibold">Your password has been updated.</p>
          {/*
            ⚠️ OFFERED, NEVER FORCED. Blocking on passkey enrollment can STRAND a
            patient: it can fail for reasons they cannot fix in the moment (wrong
            RP host — the `.web.app` trap, no platform authenticator, unsupported
            browser, the 5-credential cap), and this form has no `onSignOut`
            escape hatch. A patient who also holds SMS is still AAL2-capable and
            needs nothing.

            ⚠️ A POINTER, NOT A BUTTON. The earlier version rendered an "Add a
            passkey" button that scrolled to `PasskeyEnrollmentSection` — but
            that section is ALREADY VISIBLE just above this form, so the scroll
            moved the page barely at all and was easy to miss. Naming the section
            tells the patient where to go and needs no wiring.

            ⚠️ The heading quoted here must match
            `PasskeyEnrollmentSection.tsx:287` ("Passkeys for your account").
          */}
          {factors.hasPasskey === true && (
            <p className="mt-2 text-sm" data-testid="add-passkey-pointer">
              Your saved passkeys have been removed. You can add a new one in the{' '}
              <span className="font-semibold">Passkeys for your account</span> section above.
            </p>
          )}
          <div className="mt-3">
            <Button id="password-done" type="button" textContent="Done" onClick={() => setStage('idle')} />
          </div>
        </div>
      )}

      {stage === 'form' && (
        <form onSubmit={onSubmit} className="space-y-6">
          {/* ⚠️ BEFORE the patient commits, not after. */}
          {/*
            ⚠️ A BORDERED NOTICE, NOT BODY COPY. This sits on a security screen
            and states a consequence the patient cannot undo — it must read as a
            notice rather than blend into the form. Operator note, staging
            2026-09-23: the first version was styled as ordinary text and was
            easy to skim past.
          */}
          {warning && (
            <div
              className="rounded-md border border-amber-400 bg-amber-50 p-4"
              data-testid="password-change-warning"
              role="note"
            >
              <p className="text-base font-semibold text-amber-900">Before you continue</p>
              <p className="mt-1 text-sm text-amber-900">{warning}</p>
            </div>
          )}
          <div>
            <div className="mt-1">
              <PasswordInput
                id="password"
                title="Current Password"
                password={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <PasswordInput
                id="newPassword1"
                title="New Password"
                password={newPassword1}
                onChange={(e) => setNewPassword1(e.target.value)}
              />
              <PasswordInput
                id="newPassword2"
                title="Verify New Password"
                password={newPassword2}
                onChange={(e) => setNewPassword2(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Button id="request-update" type="submit" textContent="Update Password" />
          </div>
          {errors.length > 0 && (
            <p className="text-red-500" data-testid="password-update-error">
              {errors}
            </p>
          )}
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Button id="cancel" type="button" textContent="Cancel" onClick={handleCancel} />
            </div>
          </div>
        </form>
      )}
    </div>
  );
};
